import { Stack } from "@mui/material"
import { ReactElement } from "react"

import DemandGeneralDatabaseDevice from "./demandGeneralDatabaseDevice"
import DemandGeneralNotDatabaseDevice from "./demandGeneralNotDatabaseDevice"
import DemandHpDatabaseDevice from "./demandHpDatabaseDevice"
import GenerationGeneralDevice from "./generationGeneralDevice"
import {
    LCTOnlyDatasets,
    ProposedDeviceClasses,
} from "../../../constants/enums"
import { SUPPORT_REFER } from "../../../errors/errorMessages"
import { TDevicesToInstall } from "../../../services/middleware/mappers/dnoInterface/dnoApplicationsMapper"
import { GRID_SPACING } from "../../../themes/defaultThemeParams"
import AppAlert from "../../dataDisplay/alerts/appAlert"
import { formatKeys } from "../singleApplicationKeys"

interface IProposedDevicesProps {
    proposedDevices: TDevicesToInstall[]
}

/**
 * Displays proposed devices on the single application summary page. There are
 * multiple classes of proposed device (schemas V1 has five). This component aims
 * to cater for all of these classes, by displaying all API supplied information.
 *
 * Generally, database device will have a deviceSysRef, and non-database devices
 * will have a deviceRef.
 */
const ProposedDevices = ({
    proposedDevices,
}: IProposedDevicesProps): ReactElement => {
    if (proposedDevices.length === 0) {
        // If no existing devices
        return (
            <AppAlert severity="error">
                {`Unable to display the devices associated with this application. ${SUPPORT_REFER}`}
            </AppAlert>
        )
    }
    return (
        <Stack spacing={GRID_SPACING}>
            {proposedDevices.map((device: TDevicesToInstall) => {
                const {
                    deviceRef,
                    deviceSysRef,
                    deviceClass,
                    deviceType,
                    enaLctDataset,
                    ...details
                } = device
                if (deviceClass === ProposedDeviceClasses.genGeneral) {
                    return (
                        <GenerationGeneralDevice
                            key={`${deviceClass}${deviceSysRef}`}
                            deviceReference={
                                deviceSysRef
                                    ? String(deviceSysRef)
                                    : "Device Reference Not Supplied"
                            }
                            deviceSysRef={String(deviceSysRef)}
                            deviceType={String(deviceType)}
                            details={formatKeys(details)}
                        />
                    )
                }

                if (deviceClass === ProposedDeviceClasses.hpRegistered) {
                    return (
                        <DemandHpDatabaseDevice
                            key={`${deviceClass}${deviceSysRef}`}
                            deviceReference={
                                deviceSysRef
                                    ? String(deviceSysRef)
                                    : "Device Reference Not Supplied"
                            }
                            deviceSysRef={String(deviceSysRef)}
                            details={formatKeys(details)}
                        />
                    )
                }

                if (
                    deviceClass ===
                    ProposedDeviceClasses.demandGeneralRegistered
                ) {
                    return (
                        <DemandGeneralDatabaseDevice
                            key={`${deviceClass}${deviceSysRef}`}
                            deviceType={String(deviceType)}
                            deviceReference={
                                deviceSysRef
                                    ? String(deviceSysRef)
                                    : "Device Reference Not Supplied"
                            }
                            deviceSysRef={String(deviceSysRef)}
                            details={formatKeys(details)}
                            enaLctDataset={
                                enaLctDataset as
                                    | LCTOnlyDatasets.EVAC
                                    | LCTOnlyDatasets.EVDC
                            }
                        />
                    )
                }

                if (deviceClass === ProposedDeviceClasses.ebRegistered) {
                    return (
                        <DemandGeneralDatabaseDevice
                            key={`${deviceClass}${deviceSysRef}`}
                            deviceType={String(deviceType)}
                            deviceReference={
                                deviceSysRef
                                    ? String(deviceSysRef)
                                    : "Device Reference Not Supplied"
                            }
                            deviceSysRef={String(deviceSysRef)}
                            details={formatKeys(details)}
                            enaLctDataset={LCTOnlyDatasets.EB}
                        />
                    )
                }

                if (deviceClass === ProposedDeviceClasses.acuRegistered) {
                    return (
                        <DemandGeneralDatabaseDevice
                            key={`${deviceClass}${deviceSysRef}`}
                            deviceType={String(deviceType)}
                            deviceReference={
                                deviceSysRef
                                    ? String(deviceSysRef)
                                    : "Device Reference Not Supplied"
                            }
                            deviceSysRef={String(deviceSysRef)}
                            details={formatKeys(details)}
                            enaLctDataset={LCTOnlyDatasets.ACU}
                        />
                    )
                }
                return (
                    <DemandGeneralNotDatabaseDevice
                        key={`${deviceClass}${deviceRef}`}
                        deviceType={String(deviceType)}
                        deviceReference={
                            deviceRef
                                ? String(deviceRef)
                                : "Device Reference Not Supplied"
                        }
                        details={formatKeys(details)}
                    />
                )
            })}
        </Stack>
    )
}
export default ProposedDevices
